import { useState, useEffect, ReactNode, cloneElement, isValidElement } from "react";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { KiiFilterPanel, FilterConfig, FilterValues } from "./kii-filter-panel";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Icon } from "@/components/Icon";
import { iconNames } from "@/components/icons";
import { Input } from "@/components/ui/input";

export interface KiiListColumn {
  key: string;
  label: string;
  sortable?: boolean;
}

export type SortDirection = "asc" | "desc" | null;

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export interface ServerPagination {
  totalCount: number;
  skip: number;
  limit: number;
}

export interface KiiListProps<T = unknown> {
  title?: string | ReactNode;
  description?: string | ReactNode;
  filters?: FilterConfig[];
  searchPlaceholder?: string;
  columns: KiiListColumn[];
  data: T[];
  renderRow: (item: T, index: number) => ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  itemsPerPage?: number;
  onSearch?: (query: string, filterValues: FilterValues) => void;
  onFilterChange?: (filterValues: FilterValues) => void;
  onSearchQueryChange?: (searchQuery: string) => void;
  showCount?: boolean;
  countLabel?: (count: number) => string;
  filterValues?: FilterValues;
  searchQuery?: string;
  onRowClick?: (item: T, index: number) => void;
  actions?: ReactNode[];
  sortState?: SortState;
  onSortChange?: (sortState: SortState) => void;
  showSearch?: boolean;
  serverPagination?: ServerPagination;
  onPageChange?: (skip: number, limit: number) => void;
}

/**
 * KiiList - Self-contained list component with filters, table, and pagination
 *
 * A complete data list solution that combines:
 * - KiiFilterPanel for filtering (optional)
 * - Data table with custom row rendering
 * - Built-in pagination
 * - Loading states and empty states
 * - Item count display
 */
export function KiiList<T = unknown>({
  title,
  description,
  filters,
  searchPlaceholder = "Search...",
  columns,
  data,
  renderRow,
  isLoading = false,
  emptyMessage = "No items found",
  emptyIcon,
  itemsPerPage = 25,
  onSearch,
  onFilterChange,
  onSearchQueryChange,
  showCount = true,
  countLabel = (count: number) => `${count} item${count !== 1 ? 's' : ''} found`,
  filterValues,
  searchQuery,
  onRowClick,
  actions,
  sortState,
  onSortChange,
  showSearch = false,
  serverPagination,
  onPageChange,
}: KiiListProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);

  const isServerPaginated = !!serverPagination && !!onPageChange;

  const handleColumnSort = (columnKey: string) => {
    if (!onSortChange) return;

    if (sortState?.column === columnKey) {
      if (sortState.direction === "asc") {
        onSortChange({ column: columnKey, direction: "desc" });
      } else if (sortState.direction === "desc") {
        onSortChange({ column: null, direction: null });
      } else {
        onSortChange({ column: columnKey, direction: "asc" });
      }
    } else {
      onSortChange({ column: columnKey, direction: "asc" });
    }
  };

  const renderSortIcon = (columnKey: string) => {
    if (sortState?.column !== columnKey) {
      return <Icon name={iconNames.unfoldMore} size={14} className="text-muted-foreground/50" />;
    }
    if (sortState.direction === "asc") {
      return <Icon name={iconNames.chevronUp} size={14} className="text-foreground" />;
    }
    if (sortState.direction === "desc") {
      return <Icon name={iconNames.chevronDown} size={14} className="text-foreground" />;
    }
    return <Icon name={iconNames.unfoldMore} size={14} className="text-muted-foreground/50" />;
  };

  const effectiveItemsPerPage = isServerPaginated ? serverPagination!.limit : itemsPerPage;
  const effectiveTotalCount = isServerPaginated ? serverPagination!.totalCount : data.length;
  const totalPages = Math.ceil(effectiveTotalCount / effectiveItemsPerPage);

  const serverCurrentPage = isServerPaginated
    ? Math.floor(serverPagination!.skip / serverPagination!.limit) + 1
    : 1;

  useEffect(() => {
    if (!isServerPaginated) {
      const maxPage = Math.max(totalPages, 1);
      if (currentPage > maxPage) {
        setCurrentPage(maxPage);
      }
    }
  }, [totalPages, currentPage, isServerPaginated]);

  useEffect(() => {
    if (!isServerPaginated) {
      setCurrentPage(1);
    }
  }, [data.length, isServerPaginated]);

  const effectiveCurrentPage = isServerPaginated ? serverCurrentPage : currentPage;
  const safePage = Math.min(effectiveCurrentPage, Math.max(totalPages, 1));
  const startIndex = isServerPaginated ? 0 : (safePage - 1) * itemsPerPage;
  const endIndex = isServerPaginated ? data.length : startIndex + itemsPerPage;
  const paginatedData = isServerPaginated ? data : data.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    if (isServerPaginated) {
      const newSkip = (newPage - 1) * serverPagination!.limit;
      onPageChange!(newSkip, serverPagination!.limit);
    } else {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 5;
    const activePage = effectiveCurrentPage;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (activePage > 3) {
        pages.push('...');
      }

      for (let i = Math.max(2, activePage - 1); i <= Math.min(totalPages - 1, activePage + 1); i++) {
        pages.push(i);
      }

      if (activePage < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Card>
      {title && (
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-2xl font-semibold leading-none tracking-tight font-heading">{title}</h2>
              {description && (
                <CardDescription className="mt-1.5">
                  {description}
                </CardDescription>
              )}
            </div>
            {actions && actions.length > 0 && (
              <div className="flex gap-2 flex-wrap" data-testid="list-actions">
                {actions.map((action, index) => (
                  <div key={index}>{action}</div>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
      )}
      <CardContent className="space-y-4 pt-6">
        {filters && filters.length > 0 && (
          <KiiFilterPanel
            filters={filters}
            searchPlaceholder={searchPlaceholder}
            onSearch={onSearch}
            onFilterChange={onFilterChange}
            onSearchQueryChange={onSearchQueryChange}
            layout="grid"
            filterValues={filterValues}
            searchQuery={searchQuery}
          />
        )}

        {showSearch && !(filters && filters.length > 0) && (
          <div className="relative max-w-sm">
            <Icon
              name={iconNames.search}
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery || ""}
              onChange={(e) => onSearchQueryChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSearch?.(searchQuery || "", filterValues || {});
                }
              }}
              className="pl-9"
              data-testid="input-search"
            />
          </div>
        )}

        {showCount && (
          <p className="text-sm text-muted-foreground" data-testid="text-item-count">
            {isLoading ? "Loading..." : countLabel(effectiveTotalCount)}
          </p>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : data.length > 0 ? (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((column) => (
                      <TableHead
                        key={column.key}
                        className={column.sortable && onSortChange ? "cursor-pointer select-none hover:bg-muted/50" : ""}
                        onClick={column.sortable && onSortChange ? () => handleColumnSort(column.key) : undefined}
                        data-testid={column.sortable ? `sort-column-${column.key}` : undefined}
                      >
                        <div className="flex items-center gap-1">
                          {column.label}
                          {column.sortable && onSortChange && renderSortIcon(column.key)}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedData.map((item, index) => {
                    const rowKey = `row-${startIndex + index}`;
                    const row = renderRow(item, startIndex + index);
                    if (onRowClick && isValidElement(row)) {
                      return cloneElement(row, {
                        ...row.props,
                        key: row.key || rowKey,
                        onClick: () => onRowClick(item, startIndex + index),
                        className: `${row.props.className || ''} cursor-pointer hover:bg-muted/50`,
                        'data-testid': row.props['data-testid'] || `row-clickable-${startIndex + index}`,
                      } as React.HTMLAttributes<HTMLElement>);
                    }
                    return row;
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-2 py-4">
                <div className="text-sm text-muted-foreground">
                  {isServerPaginated ? (
                    <>Showing {serverPagination!.skip + 1} to {Math.min(serverPagination!.skip + data.length, effectiveTotalCount)} of {effectiveTotalCount}</>
                  ) : (
                    <>Showing {startIndex + 1} to {Math.min(endIndex, data.length)} of {data.length}</>
                  )}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(Math.max(1, effectiveCurrentPage - 1));
                        }}
                        className={effectiveCurrentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        data-testid="button-previous-page"
                      />
                    </PaginationItem>

                    {getPageNumbers().map((page, index) => (
                      <PaginationItem key={index}>
                        {page === '...' ? (
                          <span className="flex h-9 w-9 items-center justify-center">...</span>
                        ) : (
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(page as number);
                            }}
                            isActive={effectiveCurrentPage === page}
                            className="cursor-pointer"
                            data-testid={`button-page-${page}`}
                          >
                            {page}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}

                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(Math.min(totalPages, effectiveCurrentPage + 1));
                        }}
                        className={effectiveCurrentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        data-testid="button-next-page"
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        ) : (
          <div className="py-12 text-center">
            {emptyIcon && <div className="mx-auto mb-4">{emptyIcon}</div>}
            <p className="text-muted-foreground">{emptyMessage}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
